import ky, { HTTPError } from 'ky';
import { ConnectorError, ConnectorErrorCodes, ConnectorType, TemplateType, jsonGuard, jsonObjectGuard, tokenResponseGuard, } from './types/index.js';
export * from './types/index.js';
export * from './smtp-mailbox.js';
export function validateConfig(config, guard) {
    const result = guard.safeParse(config);
    if (!result.success) {
        throw new ConnectorError(ConnectorErrorCodes.InvalidConfig, result.error);
    }
}
export const parseJson = (jsonString, errorCode = ConnectorErrorCodes.InvalidResponse, errorPayload) => {
    try {
        return jsonGuard.parse(JSON.parse(jsonString));
    }
    catch {
        throw new ConnectorError(errorCode, errorPayload ?? jsonString);
    }
};
export const parseJsonObject = (...[jsonString, errorCode = ConnectorErrorCodes.InvalidResponse, errorPayload]) => {
    try {
        return jsonObjectGuard.parse(JSON.parse(jsonString));
    }
    catch {
        throw new ConnectorError(errorCode, errorPayload ?? jsonString);
    }
};
/**
 * The file paths for storing the mock sms/email connector records. You can use these file paths to
 * read the records for testing.
 */
export const mockConnectorFilePaths = Object.freeze({
    [ConnectorType.Sms]: '/tmp/logto/mock_sms_record.txt',
    [ConnectorType.Email]: '/tmp/logto/mock_email_record.txt',
});
/**
 * Replace all handlebars that match the keys in {@link SendMessagePayload} with the payload
 * values.
 *
 * - If the payload does not contain the root property, the handlebars will not be replaced.
 * - If the payload contains the root property but does not contain the nested property,
 *  the handlebars will be replaced with an empty string.
 *
 * @param template The template to replace the handlebars with.
 * @param payload The payload to replace the handlebars with.
 * @returns The replaced template.
 *
 * @example
 * ```ts
 * replaceSendMessageKeysWithPayload('Your verification code is {{code}}', { code: '123456' });
 * // 'Your verification code is 123456'
 *
 * replaceSendMessageKeysWithPayload('Your application name is {{application.name}}', { application: { name: 'Logto' } });
 * // 'Your application name is Logto'
 *
 * replaceSendMessageKeysWithPayload('Your application name is {{application.name}}', { application: {}});
 * // 'Your application name is '
 * ```
 *
 * @example
 * ```ts
 * replaceSendMessageKeysWithPayload('Your verification code is {{code}}', {});
 * // 'Your verification code is {{code}}'
 *
 * replaceSendMessageKeysWithPayload('Your application name is {{application.name}}', {});
 * // 'Your application name is {{application.name}}'
 * ```
 */
export const replaceSendMessageHandlebars = (template, payload) => {
    const regex = /{{\s*([\w.]+)\s*}}/g;
    return template.replaceAll(regex, (handleBar, key) => {
        const baseKey = key.split('.')[0];
        // If the root variable does not exist in the payload, return the original key
        if (!(baseKey && baseKey in payload)) {
            return handleBar;
        }
        const value = getValue(payload, key);
        return String(value ?? '');
    });
};
export const getValue = (object, path) => {
    return path.split('.').reduce((current, part) => {
        // Return undefined if the current value is not an object
        if (!current || typeof current !== 'object') {
            return;
        }
        // eslint-disable-next-line no-restricted-syntax
        return current[part];
    }, object);
};
export const getAccessTokenByRefreshToken = async ({ clientId, clientSecret, tokenEndpoint }, refreshToken) => {
    const tokenRequestParameters = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
    });
    const headers = {
        Authorization: `Basic ${Buffer.from(`${clientId}:${clientSecret}`, 'utf8').toString('base64')}`,
        'Content-Type': 'application/x-www-form-urlencoded',
    };
    try {
        const httpResponse = await ky
            .post(tokenEndpoint, {
            body: tokenRequestParameters.toString(),
            headers,
        })
            .json();
        const result = tokenResponseGuard.safeParse(httpResponse);
        if (!result.success) {
            throw new ConnectorError(ConnectorErrorCodes.InvalidResponse, result.error);
        }
        return result.data;
    }
    catch (error) {
        if (error instanceof HTTPError) {
            const { body: rawBody } = error.response;
            throw new ConnectorError(ConnectorErrorCodes.General, JSON.stringify(rawBody));
        }
        throw error;
    }
};
/**
 * Look up a connector's own inline `templates` config entry for a usage type, optionally
 * preferring one tagged with a specific `languageTag`.
 *
 * This is a same-request fallback for when the tenant-level `email_templates` table (queried via
 * `getI18nEmailTemplate`, checked first by every connector's `sendMessage`) has no match. Entries
 * without a `languageTag` are the connector's single untranslated default; existing configs have
 * none, so passing no `languageTag` (or one with no matching entry) reproduces the pre-i18n lookup
 * exactly.
 */
export function getConfigTemplateByType(type, config, languageTag) {
    const { templates } = config;
    if (!templates) {
        return;
    }
    const findByUsageType = (usageType) => (languageTag &&
        templates.find((template) => template.usageType === usageType && template.languageTag === languageTag)) ||
        templates.find((template) => template.usageType === usageType && !template.languageTag);
    return findByUsageType(type) ?? findByUsageType(TemplateType.Generic);
}
